import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { tenantApi, type NotificationDeliveryIncident } from '../../services/tenantApi';
import { SignalRService } from '../../services/signalr';
import { useRealtimeRefresh } from '../../shared/useRealtimeRefresh';
import { formatDateTime } from '../../shared/dateTimeFormatting';

type IncidentAction = { kind: 'resolve' | 'archive' | 'retest'; incident: NotificationDeliveryIncident };
export type IncidentHealth = 'checking' | 'active' | 'clear' | 'unavailable';

export function NotificationDeliveryIncidentsPanel({ canManage = false, reloadToken = 0, onHealthChanged }: {
  canManage?: boolean; reloadToken?: number; onHealthChanged?: (health: IncidentHealth) => void;
}) {
  const [incidents, setIncidents] = useState<NotificationDeliveryIncident[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [action, setAction] = useState<IncidentAction | null>(null);
  const [reason, setReason] = useState('');
  const [recipient, setRecipient] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const acting = useRef(false);
  const sequence = useRef(0);
  const panel = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async (skip = 0) => {
    const request = ++sequence.current;
    setLoading(true);
    setError('');
    try {
      const page = await tenantApi.getNotificationDeliveryIncidents({ take: 100, includeArchived, skip });
      if (request !== sequence.current) return;
      if (skip === 0) onHealthChanged?.(page.some(item => item.state === 'Active') ? 'active' : 'clear');
      setIncidents(previous => skip === 0 ? page : [
        ...previous, ...page.filter(item => !previous.some(old => old.incidentId === item.incidentId)),
      ]);
      setHasMore(page.length === 100);
    } catch (failure) {
      if (request === sequence.current) {
        setError(failure instanceof Error ? failure.message : 'Delivery incident status is unavailable or stale.');
        onHealthChanged?.('unavailable');
      }
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [includeArchived, onHealthChanged]);

  useEffect(() => {
    setIncidents([]);
    setHasMore(false);
    void refresh();
    return () => { sequence.current++; };
  }, [refresh, reloadToken]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useRealtimeRefresh(() => { if (!acting.current) void refreshRef.current(); });
  useEffect(() => SignalRService.getInstance().onNotificationDeliveryIncidentsChanged(() => {
    if (!acting.current) void refreshRef.current();
  }), []);

  useEffect(() => {
    if (!action) return;
    const previous = document.activeElement;
    panel.current?.querySelector<HTMLElement>('textarea, input, button')?.focus();
    return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus(); };
  }, [action]);

  const openAction = (kind: IncidentAction['kind'], incident: NotificationDeliveryIncident) => {
    setReason('');
    setRecipient('');
    setConfirmed(false);
    setError('');
    setNotice('');
    setAction({ kind, incident });
  };

  const perform = async (operation: () => Promise<void | string>, success: string) => {
    if (acting.current) return;
    acting.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const message = await operation();
      setNotice(message || success);
      setAction(null);
      await refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The incident action failed. Refresh and retry.');
    } finally {
      acting.current = false;
      setBusy(false);
    }
  };

  const submit = () => {
    if (!action || !canManage) return;
    const { incident, kind } = action;
    if (kind === 'resolve' && reason.trim())
      void perform(() => tenantApi.resolveNotificationDeliveryIncident(incident.incidentId, reason.trim()),
        'Incident administratively resolved. This does not confirm channel recovery. Archive it to hide it from the default list.');
    if (kind === 'archive')
      void perform(() => tenantApi.archiveNotificationDeliveryIncident(incident.incidentId),
        'Incident archived. It remains available in incident history.');
    if (kind === 'retest' && confirmed && recipient.trim())
      void perform(async () => {
        const result = await tenantApi.retestNotificationDeliveryIncident(incident.incidentId, recipient.trim());
        return `Retest queued, not yet accepted by the provider. Request ${result.requestId}. Refresh to check the submission result. No workflow was replayed.`;
      }, '');
  };

  const actionTitle = action?.kind === 'resolve' ? 'Administratively resolve incident'
    : action?.kind === 'archive' ? 'Archive incident' : 'Retest sending channel';
  return <section className="delivery-incidents-section" aria-labelledby="delivery-incidents-heading" aria-busy={loading}>
    <div className="delivery-incidents-heading">
      <h3 id="delivery-incidents-heading">Email delivery incidents</h3>
      <label><input type="checkbox" checked={includeArchived} disabled={busy}
        onChange={event => setIncludeArchived(event.target.checked)} /> Show archived incident history</label>
      <button type="button" className="notifications-icon-button" aria-label="Refresh delivery incidents"
        title="Refresh delivery incidents" disabled={loading || busy} onClick={() => void refresh()}>&#8635;</button>
    </div>
    {error && !action && <p className="notifications-view-error" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {loading && !incidents.length && <p role="status">Loading delivery incidents...</p>}
    {!loading && !error && !incidents.length && <p>No delivery incidents are available for your scope.</p>}
    <ul className="delivery-incidents-list">
      {incidents.map(incident => <li key={incident.incidentId} className={`delivery-incident-row ${incident.state.toLowerCase()}`}>
        <div className="delivery-incident-summary">
          <strong>{incident.severity} · {incident.provider} {incident.channelType}</strong>
          <span>{incident.failureClass}{incident.errorCode ? ` · ${incident.errorCode}` : ''}</span>
          <span>{incident.affectedGroupId ? 'Affected team' : 'Tenant delivery'}</span>
          <span>{incident.state === 'Resolved' ? 'Administratively resolved (recovery not verified)' : incident.state}
            {incident.archivedAt ? ' · Archived' : ''} · {incident.occurrenceCount} occurrence{incident.occurrenceCount === 1 ? '' : 's'}</span>
          <time dateTime={incident.lastSeenAt}>Last seen {formatDateTime(incident.lastSeenAt)}</time>
          {incident.lastRecoveredAt && <span>Provider recovery {formatDateTime(incident.lastRecoveredAt)}</span>}
          {incident.isAcknowledged && <span>Acknowledged by you</span>}
          {incident.resolvedAt && <span>Resolved {formatDateTime(incident.resolvedAt)} by {incident.resolvedBy} — {incident.resolutionReason}</span>}
          {incident.archivedAt && <span>Archived {formatDateTime(incident.archivedAt)} by {incident.archivedBy}</span>}
          {incident.latestRetest && <span>Latest retest: {incident.latestRetest.status === 'Succeeded' ? 'Accepted by provider' : incident.latestRetest.status}
            {' · '}{incident.latestRetest.recipientEmail} · {formatDateTime(incident.latestRetest.requestedAt)}
            {' · Requested by '}{incident.latestRetest.requestedBy} · Request {incident.latestRetest.requestId}
            {incident.latestRetest.errorCode && <> · Error {incident.latestRetest.errorCode}</>}
            {incident.latestRetest.completedAt && <> · Completed {formatDateTime(incident.latestRetest.completedAt)}</>}
          </span>}
        </div>
        <div className="delivery-incident-actions">
          {incident.state === 'Active' && !incident.isAcknowledged && <button type="button" className="btn-secondary" disabled={busy}
            onClick={() => void perform(async () => { await tenantApi.acknowledgeNotificationDeliveryIncident(incident.incidentId); },
              'Acknowledgement recorded. The incident remains active.')}>Acknowledge</button>}
          {canManage && !incident.archivedAt && <>
            {incident.state === 'Active' && <>
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => openAction('resolve', incident)}>Resolve incident</button>
              {['SMTP', 'MicrosoftGraph'].includes(incident.provider) && <button type="button" className="btn-secondary"
                disabled={busy} onClick={() => openAction('retest', incident)}>Retest channel</button>}
            </>}
            {['Recovered', 'Resolved'].includes(incident.state) && <button type="button" className="btn-secondary" disabled={busy}
              onClick={() => openAction('archive', incident)}>Archive incident</button>}
          </>}
        </div>
      </li>)}
    </ul>
    {hasMore && <button type="button" className="btn-secondary" disabled={loading || busy}
      onClick={() => void refresh(incidents.length)}>Load more incidents</button>}
    <p className="delivery-incidents-note">Acknowledgement records investigation. Provider acceptance can recover an incident.
      Administrative resolution does not verify recovery; archived incidents retain their audit history. New failures raise active incidents again.</p>
    {action && createPortal(<div className="modal-overlay" onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape' && !busy) { event.preventDefault(); setAction(null); }
      if (event.key === 'Tab') {
        const controls = panel.current?.querySelectorAll<HTMLElement>('textarea:not(:disabled), input:not(:disabled), button:not(:disabled)');
        if (!controls?.length) { event.preventDefault(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }}>
      <div className="modal modal-sm delivery-incident-dialog" role="dialog" aria-modal="true"
        aria-labelledby="incident-action-title" ref={panel}>
        <form onSubmit={event => { event.preventDefault(); submit(); }}>
          <h3 id="incident-action-title">{actionTitle}</h3>
          <p>{action.incident.provider} · Incident {action.incident.incidentId}</p>
          {action.kind === 'resolve' && <>
            <p>This records an administrative decision, not a successful email submission. A new failure can raise another incident.</p>
            <label>Resolution reason<textarea required maxLength={1000} value={reason} disabled={busy}
              onChange={event => setReason(event.target.value)} /></label>
          </>}
          {action.kind === 'archive' && <p>Hide this closed incident from the default list. Its state and audit history are retained under Show archived incident history.</p>}
          {action.kind === 'retest' && <>
            <p>This queues one real test email through the incident's original sending scope. It does not replay the failed notification.
              Provider acceptance, not queueing, can recover this incident; it does not prove inbox delivery.
              An unavailable original configuration will be rejected rather than substituted.</p>
            <label>Test recipient<input type="email" required value={recipient} disabled={busy}
              onChange={event => { setRecipient(event.target.value); setConfirmed(false); }} /></label>
            <label><input type="checkbox" checked={confirmed} disabled={busy} required
              onChange={event => setConfirmed(event.target.checked)} /> I confirm sending a real test email to this recipient.</label>
          </>}
          {error && <p className="notifications-view-error" role="alert">{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setAction(null)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy || (action.kind === 'resolve' && !reason.trim())
              || (action.kind === 'retest' && (!confirmed || !recipient.trim()))}>
              {busy ? 'Submitting...' : action.kind === 'retest' ? 'Send test email' : action.kind === 'archive' ? 'Archive incident' : 'Resolve incident'}
            </button>
          </div>
        </form>
      </div>
    </div>, document.body)}
  </section>;
}
